/*
 * Copyright (c) 2018 Integral Agile, Inc. All rights reserved.
 *   Use of this file is subject to the terms and conditions defined in the file
 * 'LICENSE.txt'
 *   which is distributed in the root directory of this source code tree.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import isEqual from 'lodash/isEqual';
import pull from 'lodash/pull';
import moment from 'moment';
import Tooltip from 'tooltip';
import Store from '../../store/store';
import modalTypes from '../../constants/modalTypes';
import { openModal } from '../../actions/modalActions';
import { hyphenate } from '../../utils/string';
import { dateRange } from '../../utils/dateHelpers';
import StoryList from '../PlanWall/storyList';
import PointSummation from '../Common/pointSummation';
import { CadenceStatuses } from '../../constants/storyStatusTypes';
import { truncate } from '../../utils/string';

class CadenceRow extends React.Component {
  constructor(props) {
    super(props);
    this.state = { visible: props.fromWall, savedState: props.preservedState };
  }

  componentWillMount() {
    this.processComponentState();
  }

  // constrain width to prevent overflow
  componentDidMount() {
    const mainWrapDiv = document.querySelector('div[ref=mainWrap]');
    mainWrapDiv.classList.add('constrain_width');
  }

  componentDidUpdate() {
    if (!isEqual(this.props.preservedState, this.state.savedState)) {
      this.processComponentState();
    }
  }

  processComponentState = () => {
    const { preservedState, cadence, fromWall } = this.props;
    let { visible } = this.state;
    const expandedSprints = preservedState.open
      ? atob(preservedState.open).split(',')
      : [];
    visible = expandedSprints.includes(`${cadence.id}`);
    if (fromWall) visible = true;
    this.setState({ visible, savedState: { ...preservedState } });
  };

  saveComponentState = () => {
    const { visible } = this.state;
    const { cadence, preservedState } = this.props;
    const expandedSprints = preservedState.open
      ? atob(preservedState.open).split(',')
      : [];
    const id = `${cadence.id}`;
    if (!visible && expandedSprints.includes(id)) pull(expandedSprints, id);
    if (visible && !expandedSprints.includes(id)) expandedSprints.push(id);
    const savedState = {
      ...preservedState,
      open: btoa(expandedSprints)
    };
    this.setState({ savedState });

    this.props.preserveComponentState(savedState);
  };

  toggleVisibility = () => {
    this.setState(
      (prevState) => ({ visible: !prevState.visible }),
      this.saveComponentState
    );
  };

  cadenceInfo = (cadence) => {
    let info = '';

    if (cadence.status === CadenceStatuses.ACCEPTED) {
      info = `ended ${moment(cadence.end_date).format('MMM D YYYY')}`;
    } else if (cadence.status === CadenceStatuses.GETTING_READY) {
      info = `starts ${moment(cadence.start_date).format('MMM D')}`;
    } else {
      const timeLeft = moment
        .duration(moment(cadence.end_date).diff(moment().startOf('day')))
        .asDays();
      const day = timeLeft < 1 ? 'today' : 'tomorrow';
      info = `ends ${timeLeft > 1 ? `in ${timeLeft} days` : day}`;
    }
    return info;
  };

  panelHeading = () => {
    const { cadence, results, fromWall } = this.props;
    const caret = this.state.visible
      ? 'glyphicon-triangle-bottom'
      : 'glyphicon-triangle-right';

    let heading = (
      <div className="panel-title" onClick={this.toggleVisibility}>
        <button type="button" className="btn btn-link">
          <span className={`glyphicon ${caret}`} aria-hidden="true" />
          <Link to={`/cardwall/cadences/${cadence.id}/workwall`}>
            {truncate(cadence.short_name || '', 10)}
          </Link>
        </button>
        <div className="right">{this.cadenceInfo(cadence)}</div>
      </div>
    );

    if (fromWall) {
      const openCadence = () =>
        Store.dispatch(
          openModal(modalTypes.cadenceDetail, {
            id: cadence.id,
            cadence
          })
        );

      heading = (
        <div className="panel-title row">
          <div className="col-md-8">
            <button
              type="button"
              className="btn btn-link  cadence_header_fragment cadence_header_1A"
              onClick={this.toggleVisibility}
            >
              <span
                className={`glyphicon ${caret} cadence_header_fragment cadence_header_1B`}
                aria-hidden="true"
              />
            </button>
            <a
              className="cadence_header_fragment cadence_header_1C "
              onClick={openCadence}
            >
              {truncate(cadence.short_name || '', 10)}
            </a>
            <small className="cadence_header_fragment cadence_header_1D ">
              {dateRange(cadence)}
            </small>
          </div>
          {/*<div className="col-md-2 cadence_header_fragment cadence_header_1E">
            <div className="center">
              <Holon />
            </div>
          </div>*/}
          <div className="col-md-4 cadence_header_fragment right cadence_header_1F">
            <PointSummation stories={results} />
          </div>
        </div>
      );
    }
    return heading;
  };

  fullRow = () => {
    const { cadence, results, showCompleted, add, remove } = this.props;

    if (!showCompleted && cadence.status === CadenceStatuses.ACCEPTED)
      return null;

    const deletedClassName = cadence.deleted_at ? 'deleted' : '';

    return (
      <div className={`panel ${hyphenate(cadence.status)} ${deletedClassName}`}>
        <div className="panel-heading">{this.panelHeading()}</div>
        <div className={`panel-body ${!this.state.visible && 'hide'}`}>
          <StoryList
            results={results}
            source={`card${cadence.id}`}
            parentId={cadence.id}
            add={add}
            delete={remove}
            mini
          />
        </div>
      </div>
    );
  };

  miniRow = () => {
    const { cadence } = this.props;
    if (
      !this.props.showCompleted &&
      cadence.status === CadenceStatuses.ACCEPTED
    )
      return null;
    const config = { showDelay: 50 };
    Tooltip(config);

    return (
      <div
        className={`panel ${hyphenate(cadence.status)}`}
        data-tooltip={cadence.short_name}
        data-tooltip-positions="left;top;bottom;right"
      >
        <div className="panel-heading">
          <h4 className="panel-title">
            {`${cadence.short_name[0]}${cadence.sprint_id}`}
          </h4>
        </div>
      </div>
    );
  };

  render() {
    return this.props.collapsed ? this.miniRow() : this.fullRow();
  }
}

CadenceRow.propTypes = {
  cadence: PropTypes.object,
  preservedState: PropTypes.object,
  results: PropTypes.array,
  fromWall: PropTypes.bool,
  showCompleted: PropTypes.bool,
  collapsed: PropTypes.bool,
  add: PropTypes.func,
  remove: PropTypes.func,
  preserveComponentState: PropTypes.func
};

export default CadenceRow;
